<template>
  <base-card>
    <template v-slot:header>
      <base-card-heading>Internal Review</base-card-heading>
      <div :class="{ 'text-danger': !approved, 'text-success': approved }">
        <span v-if="!approved">Not </span>Approved
        <base-icon :icon="approved ? 'check' : 'times'" />
      </div>
    </template>

    <form
      class="mb-0"
      @submit.prevent="submit">
      <div
        v-if="approved"
        class="text-center">
        <p class="lead">
          This was approved by <strong>{{ approved.user.profile.firstName }} {{ approved.user.profile.lastName }}</strong>.
        </p>
        <base-button
          class="mr-2"
          @click="show = !show">
          Toggle Reviews
        </base-button>
        <base-button
          danger
          @click="disapprove">
          Disapprove
        </base-button>
        <div class="border-top mt-3 pt-3" v-if="hasApprovedSlot()">
          <slot name="approved"></slot>
        </div>
      </div>
      <div v-else>
        <base-card>
          <template v-slot:header>
            <base-card-heading>Write your review</base-card-heading>
          </template>
          <wysiwyg-editor v-model="content" />
        </base-card>
        <div class="row">
          <div class="col-sm-4">
            <base-button
              v-if="reviews.length"
              @click="show = !show">
              Toggle Reviews
            </base-button>
          </div>
          <div class="col-sm-8 text-right">
            <base-button
              :disabled="content === ''"
              submit
              class="mr-2"
              @click="approve = false">
              Add Comment
            </base-button>
            <base-button
              :disabled="content === ''"
              icon-right="check"
              primary
              submit
              @click="approve = true">
              Approve
            </base-button>
          </div>
        </div>
      </div>
    </form>

    <div v-if="show && reviews.length">
      <base-table class="mt-3">
        <colgroup>
          <col/>
          <col/>
          <col/>
          <col width="300px"/>
        </colgroup>
        <thead>
          <tr>
            <th>Review</th>
            <th>Date</th>
            <th>Status</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          <template v-for="(review, key) in reviews">
            <tr :key="key">
              <td
                class="review-content"
                v-html="review.content"/>
              <td>{{ toDate(review.createdAt) }}</td>
              <td>{{ review.status }}</td>
              <td>
                <div
                  v-if="review.approved"
                  class="text-center text-success">
                  Approved <base-icon icon="check" />
                </div>
                <div v-else>
                  <AppSelect
                    :reference="review"
                    :with-group="false"
                    placeholder="Please select..."
                    prepend="Mark as"
                    source="reviewStatuses"
                    @input="transition"/>
                </div>
              </td>
            </tr>
          </template>
        </tbody>
      </base-table>
    </div>
  </base-card>
</template>

<script>
import { mapState } from 'vuex'
import { Formatting } from '@/mixins'

import AppSelect from './AppSelect.vue'
import WysiwygEditor from './WysiwygEditor.vue'

export default {
  mounted () {
    this.entity = this.$store.state.core[this.service].entity
    this.reviews = this.$_.cloneDeep(this.entity.reviews).filter(({ tag }) => tag === this.tag)

    if (this.reviews.length) {
      this.reviews.filter((review) => {
        if (review.approved && !this.disapproved) {
          this.approved = review
        }
      })

      if (!this.approved) this.show = true
    }
  },

  data () {
    return {
      approve: false,
      approved: null,
      content: '',
      entity: null,
      reviews: [],
      show: false
    }
  },

  name: 'AppReviews',

  components: {
    AppSelect,
    WysiwygEditor
  },

  mixins: [Formatting],

  props: {
    service: { required: true, type: String },
    tag: { default: '', required: true, type: String }
  },

  computed: {
    ...mapState({
      review: store => store.core.reviews.entity
    })
  },

  methods: {
    async add () {
      await this.$store.dispatch('core/reviews/save', {
        tag: this.tag,
        content: this.content,
        status: this.approve ? 'Completed' : 'Pending',
        approved: this.approve,
        user: this.$auth.user._id
      })

      const review = this.$_.cloneDeep(this.review)

      // Data returned from request won't have populated user profile.
      review.user = this.$_.cloneDeep(this.user)

      this.reviews.push(review)

      await this.$store.dispatch(`core/${this.service}/reviews/add`, this.review)

      if (this.approve) {
        this.approved = review
        this.$emit('approved')
      }

      this.$flash.success('Review successfully saved.')

      this.show = !this.approved
      this.reset()
    },

    disapprove () {
      this.approved = null
    },

    hasApprovedSlot () {
      return this.$slots.approved
    },

    ids () {
      const ids = []

      this.reviews.filter(({ _id }) => ids.push(_id))

      return ids
    },

    reset () {
      this.content = ''
    },

    submit () {
      if (this.approve) {
        this.$dialog.confirm('Are you sure you want to approve this?', () => this.add())
      } else {
        this.add()
      }
    },

    transition (value, attributes) {
      const { reference: review } = attributes
      const payload = {
        _id: review._id,
        status: value
      }

      this.reviews.filter(({ _id }) => {
        if (review._id === _id) {
          review.status = value
        }
      })

      this.$store.dispatch('core/reviews/save', payload)
    }
  }
}
</script>

<style>
    .review-content p:last-child {
        margin-bottom: 0;
    }
</style>
